<script setup>
import { onMounted, ref, computed, onUnmounted, watch } from 'vue'
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n'
import { useRouter, useRoute } from 'vue-router'
import { useModalStore } from '@/stores/modal.js'
import { useGarageStore } from '@/stores/garage.js'
import { useAuthenticationStore } from '@/stores/authentication.js'
import { useSocketStore } from '@/stores/socket.js'
import SelectVehicleDropdown from '@/components/vehicle/select_vehicle_dropdown.vue'
import { useRequestAQuoteStore } from '@/stores/request_a_quote.js'
import { indexUserAddresses } from '@/lib/api/user_addresses.js'

// Stores
const modalStore = useModalStore()
const garageStore = useGarageStore()
const authenticationStore = useAuthenticationStore()
const socketStore = useSocketStore()
const requestAQuoteStore = useRequestAQuoteStore()

// Variables
const router = useRouter()
const route = useRoute()
const isLoading = ref(true)
const isUserDropdownOpen = ref(false)
const isCarDropdownOpen = ref(false)
const userDropdown = ref(null)
const carDropdown = ref(null)
const userAddresses = ref([])
const remainingQuotes = computed(() => {
  return (authenticationStore.currentUser.user.attributes.additional_quota || 0) +
    (authenticationStore.currentUser.user.attributes.monthly_quota || 0) +
    (authenticationStore.currentUser.user.attributes.yearly_quota || 0);
})
const { currentUser } = storeToRefs(authenticationStore);

// Otto Phrases
const OTTO_FLAVOUR_FOOTER = [
  "Your go-to source for smarter auto repairs!",
  "Making car repairs easier, one quote at a time!",
  "Get smart about your car. Get AutoWise!",
  "Your trusted guide to smarter auto repairs!",
  "Helping you find the best deals for your car, every time!"
]

const currentPhrase = ref('')
const isVisible = ref(false)
const showOnClick = ref(false)
const currentPhraseIndex = ref(0)
let phraseInterval = null

// I18n
const { t } = useI18n()

onMounted(() => {
  document.addEventListener('click', closeUserDropdownOnOutsideClick)
  document.addEventListener('click', closeCarDropdownOnOutsideClick)

  if (authenticationStore.currentUser) {
    garageStore.getMyGarage()
      .then(() => {
        isLoading.value = false
      })
    getUserAddresses()
  }

  // Initialize Otto phrase with random starting index
  currentPhraseIndex.value = Math.floor(Math.random() * OTTO_FLAVOUR_FOOTER.length)
  currentPhrase.value = OTTO_FLAVOUR_FOOTER[currentPhraseIndex.value]
  
  setTimeout(() => {
    isVisible.value = true
  }, 500)
  
  // Set up 3-minute interval for phrase cycling
  phraseInterval = setInterval(() => {
    currentPhraseIndex.value = (currentPhraseIndex.value + 1) % OTTO_FLAVOUR_FOOTER.length
    currentPhrase.value = OTTO_FLAVOUR_FOOTER[currentPhraseIndex.value]
  }, 180000) // 3 minutes = 180,000ms
})

onUnmounted(() => {
  document.removeEventListener('click', closeUserDropdownOnOutsideClick)
  document.removeEventListener('click', closeCarDropdownOnOutsideClick)
  if (phraseInterval) {
    clearInterval(phraseInterval)
  }
})

const closeUserDropdownOnOutsideClick = (event) => {
  if (userDropdown.value) {
    if (!userDropdown.value.contains(event.target)) {
      isUserDropdownOpen.value = false
    }
  }
}

const closeCarDropdownOnOutsideClick = (event) => {
  if (carDropdown.value) {
    if (!carDropdown.value.contains(event.target)) {
      isCarDropdownOpen.value = false
    }
  }
}

const toggleUserDropdown = () => {
  isUserDropdownOpen.value = !isUserDropdownOpen.value
}

const toggleCarDropdown = () => {
  isCarDropdownOpen.value = !isCarDropdownOpen.value
}

const stopPropagation = (event) => {
  event.stopPropagation()
}

const closeUserDropdown = () => {
  isUserDropdownOpen.value = false
}

const closeCarDropdown = () => {
  isCarDropdownOpen.value = false
}

const handleUserClick = (event) => {
  closeCarDropdown()
  toggleUserDropdown()
  stopPropagation(event)
}

const handleCarClick = (event) => {
  closeUserDropdown()
  toggleCarDropdown()
  stopPropagation(event)
}

const userSignedIn = computed(() => {
  return authenticationStore.currentUser !== null
})

const currentUserAvatar = computed(() => {
  if (!authenticationStore.currentUser?.user?.attributes?.photo_styles?.medium) {
    return new URL('@/assets/images/temp-user.png', import.meta.url).href;
  } else {
    return decodeURIComponent(authenticationStore.currentUser.user.attributes.photo_styles.medium);
  }
});

const signOut = () => {
  authenticationStore.unauthenticateUser()
  router.push({ path: '/' })
}

const navigateMyGarage = () => {
  if (userSignedIn.value) {
    router.push({ path: '/my_garage' })
  } else {
    modalStore.setModal(modalStore.modalTypeConstants.LOGIN)
  }
}

const navigateRequestQuote = () => {
  if (userSignedIn.value) {
    requestAQuoteStore.$reset()
    router.push({ path: '/request_a_quote/step/1?reset=true' })
  } else {
    modalStore.setModal(modalStore.modalTypeConstants.LOGIN)
  }
}

const handleRequestQuoteClick = () => {
  requestAQuoteStore.$reset()
}

const currentPath = () => {
  return route.path
}

const handleLogoClick = () => {
  const randomIndex = Math.floor(Math.random() * OTTO_FLAVOUR_FOOTER.length)
  currentPhrase.value = OTTO_FLAVOUR_FOOTER[randomIndex]
  showOnClick.value = true
  
  setTimeout(() => {
    showOnClick.value = false
  }, 3000)
  
  router.push({ path: '/' })
}

const getUserAddresses = async () => {
  if (!authenticationStore.currentUser?.user?.id) return
  
  let params = new URLSearchParams()
  params.set('query[user_id_eq]', authenticationStore.currentUser.user.id)
  params.set('query[archived_eq]', 'false')
  params.set('query[is_ephemeral_eq]', 'false')

  try {
    const response = await indexUserAddresses(params)
    userAddresses.value = response
  } catch (error) {
    console.error('Failed to load user addresses:', error)
  }
}

watch(currentUser, async (newUser, oldUser) => {
  if (oldUser === null && newUser !== null) {
    garageStore.getMyGarage();
    getUserAddresses();
  }
}, { deep: true })

</script>

<template>
  <header>
    <!-- ========================================= -->
    <!-- HEADER TOP CONTAINER (Yellow Background) -->
    <!-- ========================================= -->
    <div class="header-top px-4 md:px-8 py-2">
      
      <!-- ==================== -->
      <!-- MOBILE LAYOUT (< md) -->
      <!-- ==================== -->
      <!-- Row 1: Logo (left) + User Actions (right) -->
      <div class="flex md:hidden justify-between items-center w-full mb-2">
        <!-- Mobile Logo (clickable for Otto phrases) -->
        <div class="flex items-center cursor-pointer" @click="handleLogoClick">
          <img src="../assets/images/LogoHorizontal.svg" alt="Logo 2" class="h-4">
        </div>
        
        <!-- Mobile User Section (right side) -->
        <!-- Not Signed In: Show Sign Up button -->
        <div v-if="!userSignedIn">
          <button class="px-4 py-1 rounded-full focus:outline-none text-lg font-semibold sign_up"
                  @click="modalStore.setModal(modalStore.modalTypeConstants.SIGN_UP)">
            {{ t('header.sign_up') }}
          </button>
        </div>
        <!-- Signed In: Show user avatar -->
        <div v-else class="flex space-x-2">
          <div class="user relative" ref="userDropdown">
            <img :src="currentUserAvatar" alt="Dropdown Trigger"
                 class="header-avatar-image border rounded cursor-pointer"
                 @click="handleUserClick" />
            
            <transition enter-active-class="transition ease-out duration-100"
                        leave-active-class="transition ease-in duration-75">
              <div v-if="isUserDropdownOpen"
                   class="absolute z-10 mt-2 w-56 md:w-48 bg-white rounded-md shadow-lg right-0 mr-0">
                <div class="flex items-center px-4 py-3 md:py-2 cursor-default">
                  <img
                    :src="currentUserAvatar"
                    alt="Dropdown Trigger"
                    class="dropdown-image border rounded cursor-pointer rounded w-[30px] h-[30px] md:max-w-[25px] md:max-h-[25px]"
                  />
                  <span class="block pl-3 py-2 text-base md:text-sm font-semibold text-gray-700"
                        role="menuitem">{{ authenticationStore.currentUser && authenticationStore.currentUser.user.attributes.first_name
                    }} {{ authenticationStore.currentUser && authenticationStore.currentUser.user.attributes.last_name
                    }}</span>
                </div>
                <ul
                  class="z-10 w-56 md:w-48 bg-white rounded-b-md shadow-lg right-0 mr-0 overflow-hidden"
                  @click="closeUserDropdown"
                >
                  <router-link to="/settings/orders">
                    <li class="flex block px-4 py-4 md:py-2 cursor-pointer" role="menuitem">
                      <div v-if="remainingQuotes > 0" class="inline-block text-base md:text-sm font-semibold text-gray-700 py-2">
                        {{ t('header.user_dropdown.remaining_quotes') }}
                      </div>
                      <div v-else class="text-base md:text-sm font-semibold text-gray-700 py-2">
                        {{ t('header.user_dropdown.buy_more_quotes') }}
                      </div>

                      <span v-if="remainingQuotes > 0" class="badge-sm w-[35px] h-[35px] align-middle text-center bg-blue-500 text-sm font-semibold text-white pt-2 rounded-full ml-auto">{{ remainingQuotes }}</span>
                    </li>
                  </router-link>
                  <router-link to="/my_garage/service">
                    <li class="block px-4 py-4 md:py-2 text-base md:text-sm font-semibold text-gray-700 cursor-pointer" role="menuitem">
                      {{ t('header.user_dropdown.quotes') }}
                    </li>
                  </router-link>
                  <router-link to="/settings/account">
                    <li class="block px-4 py-4 md:py-2 text-base md:text-sm font-semibold text-gray-700 cursor-pointer" role="menuitem">
                      {{ t('header.user_dropdown.settings') }}
                    </li>
                  </router-link>
                  <router-link to="/settings/notification_settings">
                    <li class="block px-4 py-4 md:py-2 text-base md:text-sm font-semibold text-gray-700 cursor-pointer" role="menuitem">
                      {{ t('header.user_dropdown.notification_center') }}
                    </li>
                  </router-link>
                  <a href="https://wiseguide.getautowise.com/contact/" target="_blank">
                    <li class="block px-4 py-4 md:py-2 text-base md:text-sm font-semibold text-gray-700 cursor-pointer" role="menuitem">
                      {{ t('header.user_dropdown.contact_us') }}
                    </li>
                  </a>
                  <li @click="signOut" class="block px-4 py-4 md:py-2 text-base md:text-sm font-semibold text-gray-700 cursor-pointer"
                      role="menuitem">{{ t('header.user_dropdown.log_out') }}
                  </li>
                </ul>
              </div>
            </transition>
          </div>
        </div>
      </div>
      

      
      <!-- ====================== -->
      <!-- DESKTOP LAYOUT (>= md) -->
      <!-- ====================== -->
      <!-- Single Row: Logo | Otto Phrases | Search + User -->
      <div class="hidden md:flex md:justify-between items-center w-full">
        
        <!-- Left Section: Logo -->
        <div class="flex items-center">
          <div class="flex items-center cursor-pointer" @click="handleLogoClick">
            <img src="../assets/images/Logomark.svg" alt="Logo 1" class="h-10 hidden lg:block mr-4">
            <img src="../assets/images/LogoHorizontal.svg" alt="Logo 2" class="h-4 lg:h-5">
          </div>
        </div>

        <!-- Center Section: Otto Phrases (expands to fill space) -->
        <div class="hidden md:flex items-center justify-center flex-1 px-4">
          <p class="text-sm font-medium transition-opacity duration-1000 text-center"
             :class="{ 'opacity-100': isVisible || showOnClick, 'opacity-0': !isVisible && !showOnClick }"
             style="color: #596672;">
            {{ currentPhrase }}
          </p>
        </div>

        <!-- Right Section: User Actions -->
        <div class="flex items-center justify-end">

          <!-- Desktop User Actions -->
          <!-- Not Signed In: Login + Sign Up buttons -->
          <div v-if="!userSignedIn" class="flex">
            <button class="px-4 focus:outline-none mr-2 text-lg font-semibold log-in"
                    @click="modalStore.setModal(modalStore.modalTypeConstants.LOGIN)">
              {{ t('header.log_in') }}
            </button>
            <button class="px-4 py-1 rounded-full focus:outline-none text-lg font-semibold sign_up"
                    @click="modalStore.setModal(modalStore.modalTypeConstants.SIGN_UP)">
              {{ t('header.sign_up') }}
            </button>
          </div>

          <!-- Signed In: Car dropdown + user avatar -->
          <div v-else class="flex space-x-2">
            <div class="car relative" ref="carDropdown">
              <img v-if="garageStore.currentVehicle && garageStore.currentVehicle.image_url"
                   :src="garageStore.currentVehicle.image_url"
                   @click="handleCarClick"
                   class="header-avatar-image border rounded cursor-pointer object-cover" />
              <div v-else
                   class="no-vehicle-container flex justify-center items-center header-avatar-image border rounded cursor-pointer"
                   @click="handleCarClick">
                <!-- <img src="../assets/icons/directions_car_40dp_FILL1_wght400_GRAD0_opsz40.svg" class="p-2 object-cover"> -->
                <img src="../assets/images/aw1.png" alt="Image 1" class="p-2 object-cover">
              </div>

              <div v-if="isCarDropdownOpen">
                <SelectVehicleDropdown :positioning-classes="'right-0'" />
              </div>
            </div>

            <div class="user relative" ref="userDropdown">
              <img
                :src="currentUserAvatar"
                alt="Dropdown Trigger"
                class="header-avatar-image border rounded cursor-pointer"
                @click="handleUserClick"
              />

              <transition enter-active-class="transition ease-out duration-100"
                          leave-active-class="transition ease-in duration-75">
                <div v-if="isUserDropdownOpen"
                     class="absolute z-10 mt-2 w-48 bg-white rounded-md shadow-lg right-0 mr-0">
                  <div class="flex items-center px-4 cursor-default">
                    <img
                      :src="currentUserAvatar"
                      alt="Dropdown Trigger"
                      class="dropdown-image border rounded cursor-pointer rounded max-w-[25px] max-h-[25px]"
                    />
                    <span class="block pl-2 py-2 text-sm font-semibold text-gray-700"
                          role="menuitem">{{ authenticationStore.currentUser && authenticationStore.currentUser.user.attributes.first_name
                      }} {{ authenticationStore.currentUser && authenticationStore.currentUser.user.attributes.last_name
                      }}</span>
                  </div>
                  <ul
                    class="z-10 w-48 bg-white rounded-b-md shadow-lg right-0 mr-0 overflow-hidden"
                    @click="closeUserDropdown"
                  >
                    <router-link to="/settings/orders">
                      <li class="flex block px-4 py-2 cursor-pointer" role="menuitem">
                        <div v-if="remainingQuotes > 0" class="inline-block text-sm font-semibold text-gray-700 py-2">
                          {{ t('header.user_dropdown.remaining_quotes') }}
                        </div>
                        <div v-else class="text-sm font-semibold text-gray-700 py-2">
                          {{ t('header.user_dropdown.buy_more_quotes') }}
                        </div>

                        <span v-if="remainingQuotes > 0" class="badge-sm w-[35px] h-[35px] align-middle text-center bg-blue-500 text-sm font-semibold text-white pt-2 rounded-full ml-auto">{{ remainingQuotes }}</span>
                      </li>
                    </router-link>
                    <router-link to="/my_garage/service">
                      <li class="block px-4 py-2 text-sm font-semibold text-gray-700 cursor-pointer" role="menuitem">
                        {{ t('header.user_dropdown.quotes') }}
                      </li>
                    </router-link>
                    <router-link to="/settings/account">
                      <li class="block px-4 py-2 text-sm font-semibold text-gray-700 cursor-pointer" role="menuitem">
                        {{ t('header.user_dropdown.settings') }}
                      </li>
                    </router-link>
                    <router-link to="/settings/notification_settings">
                      <li class="block px-4 py-2 text-sm font-semibold text-gray-700 cursor-pointer" role="menuitem">
                        {{ t('header.user_dropdown.notification_center') }}
                      </li>
                    </router-link>
                    <a href="https://wiseguide.getautowise.com/contact/" target="_blank">
                      <li class="block px-4 py-2 text-sm font-semibold text-gray-700 cursor-pointer" role="menuitem">
                        {{ t('header.user_dropdown.contact_us') }}
                      </li>
                    </a>
                    <li @click="signOut" class="block px-4 py-2 text-sm font-semibold text-gray-700 cursor-pointer"
                        role="menuitem">{{ t('header.user_dropdown.log_out') }}
                    </li>
                  </ul>
                </div>
              </transition>
            </div>
          </div>


        </div>
      </div>
    </div>

    <div class="header-bottom">
      <div class="flex flex-col md:flex-row md:justify-evenly md:items-center px-3">
        <component :is="userSignedIn ? 'router-link': 'div'" to="/my_garage" @click="navigateMyGarage"
                   :class="`flex md:justify-end items-center cursor-pointer hover:opacity-90 min-w-[250px] md:min-w-auto navbar-link py-4 px-3 ${currentPath().includes('/my_garage') ? 'router-link-active router-link-exact-active' : ''}`">
          <img src="../assets/icons/garage_home_24dp_FILL0_wght400_GRAD0_opsz24.svg" alt="Logo 1"
               class="text-white pl-4">
          <span class="px-4 focus:outline-none text-lg font-normal menu-item whitespace-nowrap">
            {{ t('header.my_garage') }}
          </span>
        </component>
        <router-link v-if="userSignedIn" to="/request_a_quote/step/1"
                     @click="handleRequestQuoteClick"
                     class="items-center blitz-a-quote rounded-full px-4 py-2 cursor-pointer hover:opacity-90 hidden md:flex min-w-[175px]"
                     :class="currentPath().includes('/request_a_quote/step/') ? 'active-link' : ''">
          <img src="../assets/images/QuoteIcon.svg" alt="Logo 1" class="text-white"
               :style="{ filter: currentPath().includes('/request_a_quote/step/') ? '' : 'brightness(0) invert(1)', height: '2em' }">
          <span class="pl-2 focus:outline-none text-lg font-normal whitespace-nowrap">
            {{ t('header.request_a_quote') }}
          </span>
        </router-link>
        <button @click="modalStore.setModal(modalStore.modalTypeConstants.LOGIN)" v-else
                class="items-center blitz-a-quote rounded-full px-4 py-2 cursor-pointer hover:opacity-90 hidden md:flex min-w-[175px]">
          <img src="../assets/images/QuoteIcon.svg" alt="Logo 1" class="text-white"
               style="height: 2em; filter: brightness(0) invert(1);">
          <span class="pl-2 focus:outline-none text-lg font-normal whitespace-nowrap">
            {{ t('header.request_a_quote') }}
          </span>
        </button>
        <router-link to="/service_providers"
                     :class="`flex items-center cursor-pointer hover:opacity-90 min-w-[250px] md:min-w-auto navbar-link py-4 px-3 ${currentPath().includes('/service_providers') ? 'router-link-active router-link-exact-active' : ''}`">
          <img src="../assets/icons/directions_car_24dp_FILL0_wght400_GRAD0_opsz24.svg" alt="Logo 1"
               class="text-white pl-4">
          <span class="px-4 focus:outline-none text-lg font-normal menu-item whitespace-nowrap">
            {{ t('header.service_providers') }}
          </span>
        </router-link>
      </div>
    </div>

    <div class="flex items-center justify-center py-2 md:hidden border-b">
      <router-link to="/request_a_quote/step/1?reset=true"
                   @click="handleRequestQuoteClick"
                   class="flex items-center blitz-a-quote shadow-md rounded-full px-4 py-2 cursor-pointer hover:opacity-90">
          <img 
            src="../assets/images/QuoteIcon.svg" 
            alt="Quote Icon" 
            class="text-white" 
            :style="route.name === 'RequestAQuote' 
                      ? { height: '2em' } 
                      : { height: '2em', filter: 'brightness(0) invert(1)' }"
          >
         <span class="pl-2 focus:outline-none text-lg font-normal">
          {{ t('header.request_a_quote') }}
        </span>
      </router-link>
    </div>
  </header>
</template>

<style lang="scss" scoped>
@import '@/assets/styles/components/header';
</style>
